import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import { DateRange } from '@material-ui/pickers';

import { Dialog, DialogTitle, Divider, Grid, IconButton, Snackbar, Tab, TableCell, TableRow, Tabs, Typography, useMediaQuery, useTheme } from '@material-ui/core';

import { Close, FileCopy } from '@material-ui/icons';

import { useStyles } from './styles';
import { PartnershipDashboardTab } from './PartnershipDashboardTab';

import { CustomTable, ExportButtonsWithTitle, TabPanel } from '../../../../components';

import { a11yProps, getTranslation } from '../../../../utils';

import { getIBPartnershipClientsAndAccounts } from '../../PartnersHierarchyService';

import { PartnershipClient } from '../../PartnersHierarchyModels';

import { useApiError } from '../../../../providers';

import { useCommonStyles } from '../../../../styles';

interface Props {
  data: any;
  open: boolean;
  handleCancel: () => void;
  translation: any;
  isDashboardEnabled: boolean;
}

interface TradingAccountRow {
  login: number | string;
  platformGroup: string;
  currency: string;
  platform: string;
  tradingAccountType: string;
}

const TAB_DIRECT_ACCOUNTS = 0;
const TAB_SUBPARTNERSHIP_ACCOUNTS = 1;
const TAB_ALL_ACCOUNTS = 3;
const TAB_DASHBOARD = 4;

const createDefaultDateRange = (): DateRange<Date> => [moment().subtract(1, 'months').toDate(), moment().toDate()];

export const ClientListDialog: FC<Props> = ({ data, open, handleCancel, translation, isDashboardEnabled }) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const commonClasses = useCommonStyles();
  const classes = useStyles();

  const { addError } = useApiError();

  const accountsColumns = useMemo(() => [translation.text_6400, translation.text_6396, translation.text_6397, translation.text_6398, translation.text_6399], [translation]);

  const [allAccountsTableData, setAllAccountsTableData] = useState<TradingAccountRow[]>([]);

  const [directAccountsTableData, setDirectAccountsTableData] = useState<TradingAccountRow[]>([]);

  const [subPartnershipAccountsTableData, setSubPartnershipAccountsTableData] = useState<TradingAccountRow[]>([]);

  const [value, setValue] = useState<number>(isDashboardEnabled ? TAB_DASHBOARD : TAB_ALL_ACCOUNTS);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>(createDefaultDateRange());

  const [emailCopied, setEmailCopied] = useState<boolean>(false);

  const clearTableData = useCallback(() => {
    setAllAccountsTableData([]);
    setDirectAccountsTableData([]);
    setSubPartnershipAccountsTableData([]);
  }, []);

  const handleCopyEmail = useCallback(async () => {
    if (!data?.email) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.email);

      setEmailCopied(true);
    } catch (error) {
      addError(JSON.stringify(error));
    }
  }, [data?.email, addError]);

  const formatReportData = useCallback((client?: PartnershipClient | null) => {
    const allAccounts: TradingAccountRow[] = [];
    const directAccounts: TradingAccountRow[] = [];
    const subPartnershipAccounts: TradingAccountRow[] = [];

    const accounts = Array.isArray(client?.accounts) ? client!.accounts : [];

    accounts.forEach((account) => {
      const accountItem: TradingAccountRow = {
        login: account.login,
        platformGroup: account.platformGroup || '-',
        currency: account.currency || '-',
        platform: account.platform || '-',
        tradingAccountType: account.tradingAccountType || '',
      };

      allAccounts.push(accountItem);

      if (account.isDirectClient) {
        directAccounts.push(accountItem);
      } else {
        subPartnershipAccounts.push(accountItem);
      }
    });

    setAllAccountsTableData(allAccounts);
    setDirectAccountsTableData(directAccounts);
    setSubPartnershipAccountsTableData(subPartnershipAccounts);
  }, []);

  const getClientsAndAccounts = useCallback(
    async (partnershipId: number) => {
      clearTableData();

      try {
        const response = await getIBPartnershipClientsAndAccounts(partnershipId);

        formatReportData(response?.data || null);
      } catch (error: any) {
        clearTableData();

        /*
         * A 403 response is expected for users who
         * cannot access the accounts endpoint.
         * Do not show a global error in that case.
         */
        if (error?.response?.status !== 403) {
          addError(JSON.stringify(error?.response || error));
        }
      }
    },
    [addError, clearTableData, formatReportData],
  );

  const translatedTradingAccounts = useMemo(() => {
    if (Array.isArray(data?.tradingAccounts) && data.tradingAccounts.length > 0) {
      return data.tradingAccounts.map((tradingAccount: string) => getTranslation(translation, tradingAccount)).join(', ');
    }

    return '-';
  }, [data?.tradingAccounts, translation]);

  /*
   * Reset modal state whenever it opens or another
   * partnership is selected.
   */
  useEffect(() => {
    if (!open || !data) {
      return;
    }

    setValue(isDashboardEnabled ? TAB_DASHBOARD : TAB_ALL_ACCOUNTS);

    setSelectedDateRange(createDefaultDateRange());

    setEmailCopied(false);
  }, [open, data?.partnershipId, isDashboardEnabled]);

  useEffect(() => {
    if (!open || data?.partnershipId === undefined || data?.partnershipId === null) {
      return;
    }

    getClientsAndAccounts(data.partnershipId);
  }, [open, data?.partnershipId, getClientsAndAccounts]);

  useEffect(() => {
    if (!isDashboardEnabled && value === TAB_DASHBOARD) {
      setValue(TAB_ALL_ACCOUNTS);
    }
  }, [isDashboardEnabled, value]);

  const handleChange = (_event: React.ChangeEvent<unknown>, newValue: number) => {
    setValue(newValue);
  };

  const renderHeader = useMemo(
    () =>
      accountsColumns.map((columnName, index) => (
        <TableCell key={`${columnName}`} align={index === 0 ? 'left' : 'center'}>
          {columnName}
        </TableCell>
      )),
    [accountsColumns],
  );

  const renderRows = useCallback(
    (rows: TradingAccountRow[]) =>
      rows.map((row) => (
        <TableRow key={`${row.login}-${row.platform}-${row.platformGroup}`}>
          <TableCell align="left" className={classes.cell}>
            {row.login}
          </TableCell>

          <TableCell align="center" className={classes.cell}>
            {row.platformGroup}
          </TableCell>

          <TableCell align="center" className={classes.cell}>
            {row.currency}
          </TableCell>

          <TableCell align="center" className={classes.cell}>
            {row.platform}
          </TableCell>

          <TableCell align="center" className={classes.cell}>
            {row.tradingAccountType ? getTranslation(translation, row.tradingAccountType) : '-'}
          </TableCell>
        </TableRow>
      )),
    [classes.cell, translation],
  );

  const renderDirectAccountRows = useMemo(() => renderRows(directAccountsTableData), [directAccountsTableData, renderRows]);

  const renderSubPartnershipAccountRows = useMemo(() => renderRows(subPartnershipAccountsTableData), [subPartnershipAccountsTableData, renderRows]);

  const renderAllAccountRows = useMemo(() => renderRows(allAccountsTableData), [allAccountsTableData, renderRows]);

  const formatExportData = useCallback(
    (rows: TradingAccountRow[]): string[][] => rows.map((row) => [String(row.login), row.platformGroup, row.currency, row.platform, row.tradingAccountType ? getTranslation(translation, row.tradingAccountType) : '-']),
    [translation],
  );

  const exportTableData = useMemo(() => {
    switch (value) {
      case TAB_DIRECT_ACCOUNTS:
        return formatExportData(directAccountsTableData);

      case TAB_SUBPARTNERSHIP_ACCOUNTS:
        return formatExportData(subPartnershipAccountsTableData);

      case TAB_ALL_ACCOUNTS:
      default:
        return formatExportData(allAccountsTableData);
    }
  }, [value, directAccountsTableData, subPartnershipAccountsTableData, allAccountsTableData, formatExportData]);

  const exportFileName = useMemo(() => {
    switch (value) {
      case TAB_DIRECT_ACCOUNTS:
        return translation.direct_trading_accounts || 'Direct Trading Accounts';

      case TAB_SUBPARTNERSHIP_ACCOUNTS:
        return translation.subpartnership_trading_accounts || 'Sub-Partnership Trading Accounts';

      case TAB_ALL_ACCOUNTS:
        return data?.type === 'Mlm' ? translation.all_trading_accounts || 'All Trading Accounts' : translation.tradingaccounts || translation.text_6407 || 'Trading Accounts';

      default:
        return translation.tradingaccounts || translation.text_6407 || 'Trading Accounts';
    }
  }, [value, data?.type, translation]);

  const reportNameInEn = useMemo(() => {
    switch (value) {
      case TAB_DIRECT_ACCOUNTS:
        return 'Direct Trading Accounts';

      case TAB_SUBPARTNERSHIP_ACCOUNTS:
        return 'Sub-Partnership Trading Accounts';

      case TAB_ALL_ACCOUNTS:
        return data?.type === 'Mlm' ? 'All Trading Accounts' : 'Trading Accounts';

      default:
        return 'Trading Accounts';
    }
  }, [value, data?.type]);

  const allAccountsTabLabel = useMemo(() => {
    if (data?.type === 'Mlm') {
      return translation.all_trading_accounts || 'All Trading Accounts';
    }

    return translation.text_6407 || translation.tradingaccounts || 'Trading Accounts';
  }, [data?.type, translation]);

  if (!data) {
    return null;
  }

  return (
    <Dialog
      onClose={handleCancel}
      open={open}
      disableEnforceFocus
      fullScreen={isMobile}
      classes={{
        paper: classes.dialog,
      }}
    >
      <div className={classes.stickyHeader}>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Grid container justify="space-between" spacing={1} alignItems="center" wrap="nowrap">
            <Grid item xs zeroMinWidth>
              <Typography variant="h6" noWrap={isMobile}>
                {`${data.title || ''} ${String(data.partnershipCode || '').toUpperCase()}`}
              </Typography>
            </Grid>

            <Grid item className={classes.dialogTitleActions}>
              {value !== TAB_DASHBOARD && <ExportButtonsWithTitle exportFileName={exportFileName} columns={accountsColumns} exportTableData={exportTableData} isEnabled reportNameInEn={reportNameInEn} />}

              <IconButton className={classes.closeBtn} onClick={handleCancel}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <Grid item xs={12} className={classes.headerDetails}>
          <Grid container direction="row" alignItems="stretch" wrap={isMobile ? 'wrap' : 'nowrap'}>
            <Grid item className={classes.topCell}>
              <Grid container direction="column">
                <Grid item className={classes.title}>
                  {translation.text_6695}
                </Grid>

                <Grid item className={classes.value}>
                  {data.name || '-'}
                </Grid>
              </Grid>
            </Grid>

            {!isMobile && <Divider orientation="vertical" flexItem />}

            <Grid item className={classes.topCell}>
              <Grid container direction="column">
                <Grid item className={classes.emailTitle}>
                  <span>{translation.text_6696}</span>

                  {data.email && (
                    <IconButton size="small" onClick={handleCopyEmail} className={classes.copyButton}>
                      <FileCopy fontSize="small" />
                    </IconButton>
                  )}
                </Grid>

                <Grid item className={`${classes.value} ${classes.emailValue}`}>
                  {data.email || '-'}
                </Grid>
              </Grid>
            </Grid>

            {!isMobile && <Divider orientation="vertical" flexItem />}

            <Grid item className={classes.topCell}>
              <Grid container direction="column">
                <Grid item className={classes.title}>
                  {translation.text_6698}
                </Grid>

                <Grid item className={classes.value}>
                  {translatedTradingAccounts}
                </Grid>
              </Grid>
            </Grid>

            {!isMobile && <Divider orientation="vertical" flexItem />}

            <Grid item className={classes.topCell}>
              <Grid container direction="column">
                <Grid item className={classes.title}>
                  {translation.text_1639}
                </Grid>

                <Grid item className={classes.value}>
                  {data.preferredCurrency || '-'}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <p className={classes.showBy}>{translation.text_6405}</p>

        <div className={classes.tabsWrapper}>
          <Tabs value={value} onChange={handleChange} className={`${commonClasses.tabs} ${classes.tabs}`} variant={isMobile ? 'scrollable' : 'fullWidth'} scrollButtons={isMobile ? 'on' : 'off'}>
            {isDashboardEnabled && <Tab label={translation.dashboard || 'Dashboard'} value={TAB_DASHBOARD} {...a11yProps(TAB_DASHBOARD)} className={classes.tab} />}

            {data.type === 'Mlm' && <Tab label={translation.direct_trading_accounts || 'Direct Trading Accounts'} value={TAB_DIRECT_ACCOUNTS} {...a11yProps(TAB_DIRECT_ACCOUNTS)} className={classes.tab} />}

            {data.type === 'Mlm' && <Tab label={translation.subpartnership_trading_accounts || 'Sub-Partnership Trading Accounts'} value={TAB_SUBPARTNERSHIP_ACCOUNTS} {...a11yProps(TAB_SUBPARTNERSHIP_ACCOUNTS)} className={classes.tab} />}

            <Tab label={allAccountsTabLabel} value={TAB_ALL_ACCOUNTS} {...a11yProps(TAB_ALL_ACCOUNTS)} className={classes.tab} />
          </Tabs>
        </div>
      </div>

      <Grid container direction="column" wrap="nowrap" className={classes.root}>
        <div className={classes.tabScrollArea}>
          {isDashboardEnabled && (
            <TabPanel value={value} index={TAB_DASHBOARD}>
              <PartnershipDashboardTab partnershipCode={data.partnershipCode} translation={translation} dateRange={selectedDateRange} onDateRangeChange={setSelectedDateRange} />
            </TabPanel>
          )}

          {data.type === 'Mlm' && (
            <>
              <TabPanel value={value} index={TAB_DIRECT_ACCOUNTS}>
                <CustomTable headerCells={renderHeader} rows={renderDirectAccountRows} columnsNumber={accountsColumns.length} />
              </TabPanel>

              <TabPanel value={value} index={TAB_SUBPARTNERSHIP_ACCOUNTS}>
                <CustomTable headerCells={renderHeader} rows={renderSubPartnershipAccountRows} columnsNumber={accountsColumns.length} />
              </TabPanel>
            </>
          )}

          <TabPanel value={value} index={TAB_ALL_ACCOUNTS}>
            <CustomTable headerCells={renderHeader} rows={renderAllAccountRows} columnsNumber={accountsColumns.length} />
          </TabPanel>
        </div>
      </Grid>

      <Snackbar
        open={emailCopied}
        autoHideDuration={2000}
        onClose={() => setEmailCopied(false)}
        message={translation.email_copied || 'Email copied!'}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      />
    </Dialog>
  );
};
